const PRADA_CART_DRAWER_TRANSITION_DURATION = 260;
const PRADA_CART_ROW_TRANSITION_DURATION = 220;

const updatePradaCartIcon = (itemCount) => {
  const safeCount = Math.max(0, Number.parseInt(itemCount || '0', 10) || 0);
  const cartLink = document.querySelector('.prada-header-btn--cart#cart-icon-bubble');

  if (cartLink) {
    let badge = cartLink.querySelector(':scope > .prada-cart-badge');

    if (safeCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'prada-cart-badge';
        badge.setAttribute('aria-hidden', 'true');
        cartLink.append(badge);
      }
      badge.textContent = String(safeCount);
    } else {
      badge?.remove();
    }

    cartLink.querySelector(':scope > .cart-count-bubble')?.remove();
    cartLink.setAttribute('aria-label', safeCount > 0 ? `Cart (${safeCount})` : 'Cart');
  }

  document.querySelectorAll('[data-prada-drawer-cart-count]').forEach((count) => {
    count.classList.toggle('is-hidden', safeCount === 0);
    if (count.lastChild) count.lastChild.textContent = String(safeCount);
    count.setAttribute('aria-label', `${safeCount} items in shopping bag`);
  });
};

const refreshPradaCartIcon = async () => {
  try {
    const cartUrl = window.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();
    if (typeof cart?.item_count === 'number') updatePradaCartIcon(cart.item_count);
  } catch (_error) {
    // Header synchronization must never interrupt a cart interaction.
  }
};

window.PradaCartHeader = window.PradaCartHeader || {};
window.PradaCartHeader.update = updatePradaCartIcon;
window.PradaCartHeader.refresh = refreshPradaCartIcon;

const isPradaCartPage = () => {
  const cartPath = new URL(window.routes?.cart_url || '/cart', window.location.origin).pathname.replace(/\/+$/, '') || '/';
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  return currentPath === cartPath;
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.revision = 0;
    this.pendingAdds = new Set();
    this.deferredCanonicalState = null;
    this.refreshTimer = null;
    this.refreshPromise = null;
    this.reconcileTimer = null;

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => this.handleDrawerClick(event), true);
    this.setHeaderCartIconAccessibility();
  }

  ownsCartState() {
    return true;
  }

  handleDrawerClick(event) {
    if (event.target.closest('#CartDrawer-Overlay')) {
      event.preventDefault();
      this.close();
      return;
    }

    const quantityButton = event.target.closest('.prada-cart-drawer__quantity-button');
    if (quantityButton && this.contains(quantityButton)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.changeItemQuantity(quantityButton, event);
      return;
    }

    const viewCartLink = event.target.closest('.prada-cart-drawer__view-cart');
    if (viewCartLink && this.contains(viewCartLink)) return;

    const destination = event.target.closest('[data-prada-fast-checkout]');
    if (!destination || !this.contains(destination)) return;

    const url = destination.dataset.pradaFastCheckout;
    if (!url) return;

    event.preventDefault();
    event.stopPropagation();
    if (destination.getAttribute('aria-busy') === 'true') return;

    if (window.PradaFastCheckout?.navigate) {
      window.PradaFastCheckout.navigate(destination);
      return;
    }

    window.location.assign(url);
  }

  setHeaderCartIconAccessibility() {
    const getCartLink = (target) => {
      if (!(target instanceof Element)) return null;
      return target.closest('#cart-icon-bubble.prada-header-btn--cart');
    };

    const openFromHeader = (event) => {
      const cartLink = getCartLink(event.target);
      if (!cartLink) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

      if (isPradaCartPage() || cartLink.dataset.pradaCartDisabled === 'true') {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      this.open(cartLink);
    };

    document.addEventListener('click', openFromHeader);
    document.addEventListener('keydown', openFromHeader);

    const cartLink = document.querySelector('#cart-icon-bubble.prada-header-btn--cart');
    if (!cartLink) return;

    if (isPradaCartPage()) {
      cartLink.setAttribute('aria-disabled', 'true');
      cartLink.dataset.pradaCartDisabled = 'true';
      cartLink.setAttribute('tabindex', '-1');
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    if (this.classList.contains('active') && !this.classList.contains('is-closing')) return;
    if (!window.PradaCartMutations?.pending) this.flushDeferredCanonicalSection();
    window.PradaFastCheckout?.prewarm?.(this.querySelector('[data-prada-fast-checkout]'));

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      triggeredBy.setAttribute('aria-expanded', 'true');
    }

    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.openFocusTimer);
    if (this.openAnimationFrame) window.cancelAnimationFrame(this.openAnimationFrame);

    this.classList.remove('is-closing');
    this.classList.add('animate', 'is-opening');
    this.classList.remove('active');

    this.openAnimationFrame = window.requestAnimationFrame(() => {
      this.openAnimationFrame = window.requestAnimationFrame(() => {
        this.openAnimationFrame = null;
        if (!this.classList.contains('is-closing')) this.classList.add('active');
      });
    });

    this.openFocusTimer = window.setTimeout(() => {
      this.openFocusTimer = null;
      this.classList.remove('is-opening');
      if (!this.classList.contains('active')) return;

      const focusContainer = this.querySelector('.drawer__inner');
      const focusElement =
        this.querySelector('.drawer__inner > .drawer__header .drawer__close') || this.querySelector('.drawer__close');
      if (focusContainer && focusElement) trapFocus(focusContainer, focusElement);
    }, PRADA_CART_DRAWER_TRANSITION_DURATION);

    if (window.pradaDrawerScrollLock) {
      window.pradaDrawerScrollLock.lock();
    } else {
      document.body.classList.add('overflow-hidden');
    }

    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    if (this.classList.contains('is-closing')) return;

    if (this.openAnimationFrame) {
      window.cancelAnimationFrame(this.openAnimationFrame);
      this.openAnimationFrame = null;
    }
    window.clearTimeout(this.openFocusTimer);
    window.clearTimeout(this.closeTimer);

    const finishClose = () => {
      this.closeTimer = null;
      this.classList.remove('active', 'animate', 'is-closing', 'is-opening');
      removeTrapFocus(this.activeElement);
      this.activeElement?.setAttribute?.('aria-expanded', 'false');

      if (window.pradaDrawerScrollLock) {
        window.pradaDrawerScrollLock.unlock();
      } else {
        document.body.classList.remove('overflow-hidden');
      }

      if (!window.PradaCartMutations?.pending) this.flushDeferredCanonicalSection();
    };

    if (!this.classList.contains('active') && !this.classList.contains('animate')) {
      finishClose();
      return;
    }

    this.classList.remove('is-opening', 'active');
    this.classList.add('is-closing');
    this.closeTimer = window.setTimeout(finishClose, PRADA_CART_DRAWER_TRANSITION_DURATION);
  }

  beginOptimisticAdd(item, triggeredBy) {
    if (!item) return null;

    const quantity = Math.max(1, Number.parseInt(item.quantity || '1', 10) || 1);
    const previousCount = this.getDisplayedItemCount();
    const previousTotal = this.getDisplayedTotal();
    const row = this.findRowByVariant(item.variantId);
    const created = !row;
    const targetRow = row || this.createCartRow(item, quantity);
    const previousQuantity = created ? 0 : this.getRowQuantity(targetRow);
    const lineReference = targetRow._pradaLineReference || this.createLineReference(targetRow, item.variantId);
    targetRow._pradaLineReference = lineReference;
    targetRow.dataset.cartVariantId = String(item.variantId);
    targetRow.dataset.cartQuantity = String(previousQuantity + quantity);
    if (item.priceCents) targetRow.dataset.cartUnitPrice = String(item.priceCents);

    if (created) {
      this.ensureCartRowsContainer().prepend(targetRow);
    } else {
      this.updateRowQuantity(targetRow, previousQuantity + quantity);
      const body = targetRow.parentElement;
      if (body?.firstElementChild !== targetRow) body?.prepend(targetRow);
      const image = targetRow.querySelector('.cart-item__image');
      if (image && item.image) {
        image.src = item.image;
        image.removeAttribute('srcset');
      }
    }

    const state = {
      id: `${Date.now()}-${Math.random()}`,
      revision: ++this.revision,
      item,
      row: targetRow,
      created,
      quantity,
      previousQuantity,
      previousCount,
      previousTotal,
      lineReference,
      settled: false,
    };

    this.pendingAdds.add(state);
    this.showPopulatedState(previousCount + quantity, previousTotal + (item.priceCents || 0) * quantity);
    this.updateMultipleLayout();
    this.setActiveElement(triggeredBy);
    this.open(triggeredBy);
    return state;
  }

  confirmOptimisticAdd(state, response) {
    if (!state || state.settled) return;
    state.settled = true;
    this.pendingAdds.delete(state);

    const lineKey = response?.key || response?.id;
    if (lineKey) {
      this.setRowLineKey(state.row, lineKey);
      state.lineReference.resolveKey?.(String(lineKey));
    }

    if (state.row?.isConnected && Number.isFinite(response?.final_price)) {
      this.updateRowUnitPrice(state.row, response.final_price);
    }

    if (Number.isFinite(response?.quantity) && state.row?.isConnected) {
      this.updateRowQuantity(state.row, response.quantity);
    }

    if (state.created && state.row?.isConnected && lineKey) {
      const stepper = state.row.querySelector('.prada-cart-drawer__quantity-stepper');
      const input = state.row.querySelector('.prada-cart-drawer__quantity-input');
      if (input) {
        input.disabled = false;
        input.readOnly = true;
      }
      state.row.querySelectorAll('.prada-cart-drawer__quantity-button').forEach((button) => {
        button.disabled = false;
      });
      stepper?.removeAttribute('aria-busy');
      this.updateQuantityControlState(state.row);
    }

  }

  cancelOptimisticAdd(state) {
    if (!state || state.settled) return;
    state.settled = true;
    state.lineReference.failed = true;
    state.lineReference.rejectKey?.(new Error('Add to cart failed'));
    this.pendingAdds.delete(state);

    if (state.row?.isConnected && state.row.dataset.cartRemovePending !== 'true') {
      if (state.created) {
        state.row.remove();
      } else {
        this.updateRowQuantity(state.row, state.previousQuantity);
      }
      this.setDisplayedTotals(state.previousCount, state.previousTotal);
      if (state.previousCount === 0) this.showEmptyState({ animate: false });
      this.updateMultipleLayout();
    }
  }

  scheduleRefreshAfterOptimisticAdd(_state, { after } = {}) {
    Promise.resolve(after)
      .then((cart) => {
        if (typeof cart?.item_count !== 'number') return;
        this.setDisplayedTotals(cart.item_count, cart.total_price);
        const row = _state?.row;
        const line = this.findCartLine(cart, row, _state?.lineReference?.key);
        if (line && row?.isConnected) this.syncRowFromCartLine(row, line);
      })
      .catch(() => undefined)
      .finally(() => this.requestCanonicalRefresh());
  }

  removeItem(removeButton, event) {
    const row = removeButton?.closest('.cart-item');
    if (!row || row.dataset.cartRemovePending === 'true') return;

    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (row._pradaQuantityState) {
      window.clearTimeout(row._pradaQuantityState.timer);
      row._pradaQuantityState.timer = null;
      row._pradaQuantityState.revision += 1;
    }

    const lineReference = row._pradaLineReference || this.createLineReference(row);
    row._pradaLineReference = lineReference;
    const quantity = this.getRowQuantity(row);
    const unitPrice = Number.parseInt(row.dataset.cartUnitPrice || '0', 10) || 0;
    const previousCount = this.getDisplayedItemCount();
    const previousTotal = this.getDisplayedTotal();
    const actionRevision = ++this.revision;
    const parent = row.parentElement;
    const nextSibling = row.nextElementSibling;

    row.dataset.cartRemovePending = 'true';
    this.setDrawerBusy(true);
    row.querySelectorAll('button, a').forEach((control) => {
      control.setAttribute('aria-disabled', 'true');
      if ('disabled' in control) control.disabled = true;
    });

    const nextCount = Math.max(0, previousCount - quantity);
    const nextTotal = Math.max(0, previousTotal - unitPrice * quantity);
    this.setDisplayedTotals(nextCount, nextTotal);
    this.animateRowRemoval(row, nextCount === 0);
    if (event) CartPerformance.measureFromEvent('remove:optimistic-ui', event);

    const removeRequest = async () => {
      let lineKey = lineReference.key;
      if (!lineKey && lineReference.keyPromise) lineKey = await lineReference.keyPromise;
      return this.changeCartLine(row, lineKey, 0);
    };

    const promise = window.PradaCartMutations?.enqueue
      ? window.PradaCartMutations.enqueue(removeRequest)
      : removeRequest();

    promise
      .then((cart) => {
        if (actionRevision === this.revision) {
          this.setDisplayedTotals(cart.item_count, cart.total_price);
          if (
            cart.item_count === 0 &&
            !this.classList.contains('is-empty') &&
            !this.classList.contains('is-empty-pending')
          ) {
            this.showEmptyState({ animate: false });
          }
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: cart });
        this.requestCanonicalRefresh();
      })
      .catch((error) => {
        if (!lineReference.failed) {
          this.restoreRemovedRow({ row, parent, nextSibling, previousCount, previousTotal });
        }
        this.showCartError(error?.message || window.cartStrings?.error || 'Cart update failed');
        this.requestCanonicalRefresh();
      })
      .finally(() => {
        const clearBusy = () => this.setDrawerBusy(false);
        if (window.PradaCartMutations?.pending) {
          window.PradaCartMutations.whenIdle().then(clearBusy);
        } else {
          clearBusy();
        }
      });
  }

  animateRowRemoval(row, isLastItem) {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const duration = reducedMotion ? 0 : PRADA_CART_ROW_TRANSITION_DURATION;
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const stagesMobileEmptyState = Boolean(isLastItem && isMobile);
    const scroller = isMobile ? row.closest('cart-drawer-items') : null;
    const carouselRows = scroller
      ? [...row.parentElement.querySelectorAll('.cart-item')].filter(
          (item) => item === row || item.dataset.cartRemovePending !== 'true',
        )
      : [];
    const removedIndex = carouselRows.indexOf(row);

    if (scroller) {
      // Keep every slide's width intact while it fades. Collapsing a flex item
      // inside a snap container makes mobile browsers fling the scroll position.
      scroller.classList.add('is-repositioning');
    } else {
      row.style.maxHeight = `${row.offsetHeight}px`;
      row.style.overflow = 'hidden';
      row.getBoundingClientRect();
    }

    if (stagesMobileEmptyState) {
      this.querySelector('.drawer__inner-empty')?.removeAttribute('hidden');
      this.classList.add('is-empty-pending');
    } else if (isLastItem) {
      this.showEmptyState({ animate: true });
    }
    window.requestAnimationFrame(() => {
      row.classList.add('is-removing');
      if (!scroller) row.classList.add('is-collapsing');
      if (stagesMobileEmptyState && this.classList.contains('is-empty-pending')) {
        this.classList.add('is-empty-revealed');
      }
    });

    window.setTimeout(() => {
      if (row.dataset.cartRemovePending !== 'true') return;
      row.remove();
      this.updateMultipleLayout();
      if (scroller && !isLastItem) {
        const remainingRows = [...scroller.querySelectorAll('.cart-item:not([data-cart-remove-pending="true"])')];
        const targetIndex = Math.min(Math.max(removedIndex, 0), remainingRows.length - 1);
        const firstRowLeft = remainingRows[0]?.offsetLeft || 0;
        const targetRowLeft = remainingRows[targetIndex]?.offsetLeft || firstRowLeft;
        scroller.scrollLeft = Math.max(0, targetRowLeft - firstRowLeft);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => scroller.classList.remove('is-repositioning'));
        });
      } else {
        scroller?.classList.remove('is-repositioning');
      }
      if (stagesMobileEmptyState) {
        this.classList.remove('is-empty-pending');
        this.showEmptyState({ animate: false });
      }
      if (this.classList.contains('active')) {
        const focusTarget = isLastItem
          ? this.querySelector('.drawer__inner > .drawer__header .drawer__close')
          : this.querySelector('.cart-item:not([data-cart-remove-pending="true"]) .prada-cart-drawer__remove');
        focusTarget?.focus({ preventScroll: true });
      }
    }, duration);
  }

  restoreRemovedRow({ row, parent, nextSibling, previousCount, previousTotal }) {
    if (!row.isConnected && parent?.isConnected) parent.insertBefore(row, nextSibling?.isConnected ? nextSibling : null);
    delete row.dataset.cartRemovePending;
    row.classList.remove('is-removing', 'is-collapsing', 'is-horizontal-removal');
    row.style.maxHeight = '';
    row.style.maxWidth = '';
    row.style.width = '';
    row.style.flexBasis = '';
    row.style.overflow = '';
    row.querySelectorAll('button, a').forEach((control) => {
      control.removeAttribute('aria-disabled');
      if ('disabled' in control) control.disabled = false;
    });
    this.showPopulatedState(previousCount, previousTotal);
    this.updateMultipleLayout();
  }

  createLineReference(row, fallbackVariantId) {
    const existingKey = row?.dataset.cartLineKey || row?.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
    let resolveKey;
    let rejectKey;
    const keyPromise = existingKey
      ? Promise.resolve(existingKey)
      : new Promise((resolve, reject) => {
          resolveKey = resolve;
          rejectKey = reject;
        });
    keyPromise.catch(() => undefined);

    return {
      key: existingKey || null,
      variantId: row?.dataset.cartVariantId || fallbackVariantId || null,
      keyPromise,
      resolveKey,
      rejectKey,
      failed: false,
    };
  }

  setRowLineKey(row, lineKey) {
    if (!row || !lineKey) return;
    const key = String(lineKey);
    row.dataset.cartLineKey = key;
    row.querySelectorAll('cart-remove-button').forEach((button) => {
      button.dataset.lineKey = key;
    });
    row.querySelectorAll('[data-quantity-line-key]').forEach((input) => {
      input.dataset.quantityLineKey = key;
    });

    if (row._pradaLineReference) row._pradaLineReference.key = key;
    if (row._pradaQuantityState) row._pradaQuantityState.lineKey = key;
  }

  updateRowUnitPrice(row, unitPrice) {
    const price = Number.parseInt(unitPrice || '0', 10);
    if (!row || !Number.isFinite(price) || price < 0) return;
    row.dataset.cartUnitPrice = String(price);
    const priceElement = row.querySelector('.prada-cart-drawer__price');
    if (priceElement) priceElement.textContent = this.formatMoney(price, priceElement.textContent);
  }

  findCartLine(cart, row, preferredKey, preferredQuantity) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (preferredKey) {
      const exact = items.find((item) => String(item.key) === String(preferredKey));
      if (exact) return exact;
    }

    const variantId = row?.dataset.cartVariantId || row?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId;
    const candidates = items.filter((item) => String(item.variant_id || item.id) === String(variantId || ''));
    if (candidates.length <= 1) return candidates[0] || null;

    const linePosition = Number.parseInt(row?.dataset.cartLinePosition || '', 10);
    const positional = Number.isFinite(linePosition) ? items[linePosition - 1] : null;
    if (positional && candidates.includes(positional)) return positional;

    const quantity = Number.parseInt(preferredQuantity || row?.dataset.cartQuantity || '', 10);
    return candidates.find((item) => item.quantity === quantity) || candidates[0] || null;
  }

  syncRowFromCartLine(row, line) {
    if (!row || !line) return;
    this.setRowLineKey(row, line.key);
    row.dataset.cartVariantId = String(line.variant_id || line.id || row.dataset.cartVariantId || '');
    this.updateRowQuantity(row, line.quantity);
    this.updateRowUnitPrice(row, line.final_price ?? line.price);
  }

  isInvalidLineError(cart) {
    const message = [cart?.description, cart?.message, cart?.errors]
      .filter(Boolean)
      .map((value) => (typeof value === 'string' ? value : JSON.stringify(value)))
      .join(' ');
    return /no valid id|valid id or line|line parameter|invalid.*(?:id|line)/i.test(message);
  }

  async fetchCurrentCart() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(window.cartStrings?.error || 'Cart refresh failed');
    return response.json();
  }

  async changeCartLine(row, requestedKey, quantity) {
    const sendChange = async (lineKey) => {
      if (!lineKey) return { response: null, cart: { description: 'No valid id or line parameter' } };
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        cache: 'no-store',
        body: JSON.stringify({ id: String(lineKey), quantity }),
      });
      let cart;
      try {
        cart = await response.json();
      } catch (_error) {
        cart = { description: window.cartStrings?.error || 'Cart update failed' };
      }
      return { response, cart };
    };

    let result = await sendChange(requestedKey);
    if (!result.response?.ok || result.cart?.errors || result.cart?.status) {
      if (!this.isInvalidLineError(result.cart)) {
        throw new Error(
          result.cart?.description ||
            result.cart?.message ||
            result.cart?.errors ||
            window.cartStrings?.error ||
            'Cart update failed',
        );
      }

      const currentCart = await this.fetchCurrentCart();
      const currentLine = this.findCartLine(currentCart, row, requestedKey);
      if (!currentLine?.key) throw new Error(window.cartStrings?.error || 'Cart item is no longer available');
      this.setRowLineKey(row, currentLine.key);
      result = await sendChange(currentLine.key);
    }

    if (!result.response?.ok || result.cart?.errors || result.cart?.status) {
      throw new Error(
        result.cart?.description ||
          result.cart?.message ||
          result.cart?.errors ||
          window.cartStrings?.error ||
          'Cart update failed',
      );
    }

    return result.cart;
  }

  findRowByVariant(variantId) {
    return [...this.querySelectorAll('#CartDrawer-CartItems .cart-item:not([data-cart-remove-pending="true"])')].find(
      (row) => String(row.dataset.cartVariantId || row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId || '') === String(variantId),
    );
  }

  getRowQuantity(row) {
    const explicit = Number.parseInt(row?.dataset.cartQuantity || '', 10);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const input = Number.parseInt(row?.querySelector('[data-quantity-variant-id]')?.value || '', 10);
    if (Number.isFinite(input) && input > 0) return input;
    const label = row?.querySelector('.prada-cart-drawer__quantity')?.textContent || '';
    return Number.parseInt(label.match(/\d+/)?.[0] || '1', 10) || 1;
  }

  updateRowQuantity(row, quantity) {
    row.dataset.cartQuantity = String(quantity);
    row.querySelectorAll('.quantity__input').forEach((input) => {
      input.value = String(quantity);
      input.setAttribute('value', String(quantity));
    });
    this.updateQuantityControlState(row);
  }

  updateQuantityControlState(row) {
    const input = row?.querySelector('.prada-cart-drawer__quantity-input');
    if (!input) return;
    const value = Number.parseInt(input.value || '0', 10) || 0;
    const minimum = Number.parseInt(input.dataset.min || input.min || '1', 10) || 1;
    const maximum = input.max ? Number.parseInt(input.max, 10) : null;
    const minus = row.querySelector('.prada-cart-drawer__quantity-button[name="minus"]');
    const plus = row.querySelector('.prada-cart-drawer__quantity-button[name="plus"]');
    minus?.classList.toggle('disabled', value <= minimum);
    plus?.classList.toggle('disabled', Number.isFinite(maximum) && value >= maximum);
  }

  changeItemQuantity(button, event) {
    const row = button.closest('.cart-item');
    const input = row?.querySelector('.prada-cart-drawer__quantity-input');
    const lineKey = row?.dataset.cartLineKey || input?.dataset.quantityLineKey;
    if (!row || !input || !lineKey || button.disabled || button.classList.contains('disabled')) return;

    const currentQuantity = Math.max(1, Number.parseInt(input.value || row.dataset.cartQuantity || '1', 10) || 1);
    const step = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
    const minimum = Math.max(1, Number.parseInt(input.dataset.min || input.min || '1', 10) || 1);
    const maximum = input.max ? Number.parseInt(input.max, 10) : null;
    const requestedQuantity = button.name === 'plus' ? currentQuantity + step : currentQuantity - step;
    const nextQuantity = Number.isFinite(maximum)
      ? Math.min(maximum, Math.max(minimum, requestedQuantity))
      : Math.max(minimum, requestedQuantity);
    if (nextQuantity === currentQuantity) return;

    // Any cached section HTML was rendered before this click. Invalidate it
    // so closing/reopening the drawer can never restore a stale quantity.
    this.revision += 1;
    this.deferredCanonicalState = null;

    const unitPrice = Number.parseInt(row.dataset.cartUnitPrice || '0', 10) || 0;
    const delta = nextQuantity - currentQuantity;
    this.updateRowQuantity(row, nextQuantity);
    this.setDisplayedTotals(
      Math.max(0, this.getDisplayedItemCount() + delta),
      Math.max(0, this.getDisplayedTotal() + unitPrice * delta),
    );
    CartPerformance.measureFromEvent?.('quantity:optimistic-ui', event);

    const state = row._pradaQuantityState || {
      revision: 0,
      timer: null,
      pending: 0,
    };
    row._pradaQuantityState = state;
    state.desiredQuantity = nextQuantity;
    state.lineKey = String(lineKey);
    state.revision += 1;
    const revision = state.revision;

    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      state.timer = null;
      this.commitItemQuantity(row, state, revision);
    }, 70);
  }

  commitItemQuantity(row, state, revision) {
    if (!row?.isConnected || revision !== state.revision) return;
    const requestedQuantity = state.desiredQuantity;
    const stepper = row.querySelector('.prada-cart-drawer__quantity-stepper');
    state.pending += 1;
    stepper?.setAttribute('aria-busy', 'true');

    const changeRequest = async () => {
      return this.changeCartLine(row, state.lineKey, requestedQuantity);
    };

    const promise = window.PradaCartMutations?.enqueue
      ? window.PradaCartMutations.enqueue(changeRequest)
      : changeRequest();

    promise
      .then((cart) => {
        if (revision !== state.revision || !row.isConnected) return;
        const confirmedLine = this.findCartLine(cart, row, state.lineKey, requestedQuantity);
        const confirmedQuantity = Number.parseInt(confirmedLine?.quantity || requestedQuantity, 10) || requestedQuantity;
        if (confirmedLine) this.syncRowFromCartLine(row, confirmedLine);
        else this.updateRowQuantity(row, confirmedQuantity);
        this.setDisplayedTotals(cart.item_count, cart.total_price);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-quantity',
          cartData: cart,
          variantId: row.dataset.cartVariantId,
        });
      })
      .catch((error) => {
        if (revision !== state.revision || !row.isConnected) return;
        this.showCartError(error?.message || window.cartStrings?.error || 'Cart update failed');
        this.restoreQuantityFromCart(row, state, revision);
      })
      .finally(() => {
        state.pending = Math.max(0, state.pending - 1);
        if (state.pending === 0 && row.isConnected) stepper?.removeAttribute('aria-busy');
      });
  }

  async restoreQuantityFromCart(row, state, revision) {
    try {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      const cart = await response.json();
      if (!row.isConnected || revision !== state.revision) return;
      const line = cart.items?.find((item) => String(item.key) === state.lineKey);
      if (line) this.updateRowQuantity(row, line.quantity);
      this.setDisplayedTotals(cart.item_count, cart.total_price);
    } catch (_error) {
      this.requestCanonicalRefresh();
    }
  }

  ensureCartRowsContainer() {
    const contents = this.querySelector('#CartDrawer-CartItems');
    let body = contents?.querySelector('.prada-cart-drawer__items tbody');
    if (body) return body;

    const wrapper = document.createElement('div');
    wrapper.className = 'drawer__cart-items-wrapper';
    const table = document.createElement('table');
    table.className = 'cart-items prada-cart-drawer__items';
    table.setAttribute('role', 'table');
    body = document.createElement('tbody');
    body.setAttribute('role', 'rowgroup');
    table.append(body);
    wrapper.append(table);
    contents?.prepend(wrapper);
    return body;
  }

  createCartRow(item, quantity) {
    const row = document.createElement('tr');
    row.className = 'cart-item prada-cart-drawer__optimistic-new-item';
    row.dataset.cartVariantId = String(item.variantId);
    row.dataset.cartQuantity = String(quantity);
    row.dataset.cartUnitPrice = String(item.priceCents || 0);
    row.setAttribute('role', 'row');

    const media = document.createElement('td');
    media.className = 'cart-item__media';
    media.setAttribute('role', 'cell');
    if (item.image) {
      const link = document.createElement('a');
      link.className = 'cart-item__link';
      link.href = item.url || '#';
      link.tabIndex = -1;
      link.setAttribute('aria-hidden', 'true');
      const image = document.createElement('img');
      image.className = 'cart-item__image';
      image.src = item.image;
      image.alt = item.imageAlt || item.title;
      image.width = 150;
      image.height = 188;
      image.decoding = 'async';
      link.append(image);
      media.append(link);
    }

    const details = document.createElement('td');
    details.className = 'cart-item__details';
    details.setAttribute('role', 'cell');
    const titleWrap = document.createElement('div');
    titleWrap.className = 'cart-item__title';
    const title = document.createElement('a');
    title.className = 'cart-item__name h4 break';
    title.href = item.url || '#';
    title.textContent = item.title;
    titleWrap.append(title);
    details.append(titleWrap);

    const info = document.createElement('div');
    info.className = 'prada-cart-drawer__item-info';
    if (item.color) {
      const color = document.createElement('p');
      color.className = 'prada-cart-drawer__color';
      color.textContent = item.color;
      info.append(color);
    }
    if (item.price || item.compareAtPrice) {
      const priceRow = document.createElement('div');
      priceRow.className = 'prada-cart-drawer__price-row';
      if (item.compareAtPrice) {
        const compareAtPrice = document.createElement('s');
        compareAtPrice.className = 'prada-cart-drawer__compare-price money';
        compareAtPrice.textContent = item.compareAtPrice;
        priceRow.append(compareAtPrice);
      }
      if (item.price) {
        const price = document.createElement('span');
        price.className = 'prada-cart-drawer__price money';
        price.textContent = item.price;
        priceRow.append(price);
      }
      info.append(priceRow);
    }
    const orderedOptions = [...(item.options || [])].sort((left, right) => {
      const leftIsSize = String(left.name || '').toLowerCase().includes('size');
      const rightIsSize = String(right.name || '').toLowerCase().includes('size');
      return Number(rightIsSize) - Number(leftIsSize);
    });
    orderedOptions.filter((option) => !/colou?r/i.test(String(option.name || ''))).forEach((option) => {
      const optionRow = document.createElement('p');
      optionRow.className = 'prada-cart-drawer__option';
      optionRow.textContent = option.value;
      info.append(optionRow);
    });
    const actions = document.createElement('div');
    actions.className = 'prada-cart-drawer__actions';
    const quantityControl = document.createElement('div');
    quantityControl.className = 'prada-cart-drawer__quantity-control';
    const quantityStepper = document.createElement('quantity-input');
    quantityStepper.className = 'prada-cart-drawer__quantity-stepper';
    quantityStepper.setAttribute('aria-busy', 'true');
    const quantityMinus = document.createElement('button');
    quantityMinus.className = 'quantity__button prada-cart-drawer__quantity-button';
    quantityMinus.name = 'minus';
    quantityMinus.type = 'button';
    quantityMinus.disabled = true;
    quantityMinus.setAttribute('aria-label', `Decrease quantity for ${item.title}`);
    quantityMinus.textContent = '−';
    const quantityInput = document.createElement('input');
    quantityInput.className = 'quantity__input prada-cart-drawer__quantity-input';
    quantityInput.type = 'number';
    quantityInput.name = 'updates[]';
    quantityInput.value = String(quantity);
    quantityInput.setAttribute('value', String(quantity));
    quantityInput.min = '1';
    quantityInput.dataset.min = '1';
    quantityInput.step = '1';
    quantityInput.disabled = true;
    quantityInput.readOnly = true;
    quantityInput.dataset.quantityVariantId = String(item.variantId);
    quantityInput.setAttribute('aria-label', `Quantity for ${item.title}`);
    const quantityPlus = document.createElement('button');
    quantityPlus.className = 'quantity__button prada-cart-drawer__quantity-button';
    quantityPlus.name = 'plus';
    quantityPlus.type = 'button';
    quantityPlus.disabled = true;
    quantityPlus.setAttribute('aria-label', `Increase quantity for ${item.title}`);
    quantityPlus.textContent = '+';
    quantityStepper.append(quantityMinus, quantityInput, quantityPlus);
    quantityControl.append(quantityStepper);
    actions.append(quantityControl);
    const removeWrap = document.createElement('cart-remove-button');
    removeWrap.className = 'prada-cart-drawer__remove-wrap';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'prada-cart-drawer__remove';
    remove.textContent = 'Remove';
    remove.setAttribute('aria-label', `Remove ${item.title}`);
    removeWrap.append(remove);
    actions.append(removeWrap);
    info.append(actions);
    details.append(info);
    row.append(media, details);
    return row;
  }

  getDisplayedItemCount() {
    return Math.max(0, Number.parseInt(this.dataset.cartItemCount || '0', 10) || 0);
  }

  getDisplayedTotal() {
    return Math.max(0, Number.parseInt(this.dataset.cartTotalPrice || '0', 10) || 0);
  }

  setDisplayedTotals(itemCount, totalPrice) {
    const count = Math.max(0, Number.parseInt(itemCount || '0', 10) || 0);
    const total = Math.max(0, Number.parseInt(totalPrice || '0', 10) || 0);
    this.dataset.cartItemCount = String(count);
    this.dataset.cartTotalPrice = String(total);
    updatePradaCartIcon(count);

    const desktopHeading = this.querySelector('.drawer__inner > .drawer__header .prada-cart-drawer__heading-desktop');
    const mobileHeading = this.querySelector('.drawer__inner > .drawer__header .prada-cart-drawer__heading-mobile');
    if (desktopHeading) desktopHeading.textContent = `Your selection (${count})`;
    if (mobileHeading) mobileHeading.textContent = `Added to shopping bag (${count})`;

    const subtotal = this.querySelector('.drawer__inner > .drawer__footer .totals__total-value');
    if (subtotal) subtotal.textContent = this.formatMoney(total, subtotal.textContent);
  }

  showPopulatedState(itemCount, totalPrice) {
    window.clearTimeout(this.emptyTimer);
    this.classList.remove(
      'is-empty',
      'is-empty-pending',
      'is-empty-stable',
      'is-empty-transitioning',
      'is-empty-revealed',
      'is-empty-entering',
      'is-empty-visible',
      'is-empty-leaving',
    );
    this.querySelector('.drawer__inner-empty')?.setAttribute('hidden', '');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = false;
    this.setDisplayedTotals(itemCount, totalPrice);
  }

  showEmptyState({ animate = true } = {}) {
    window.clearTimeout(this.emptyTimer);
    const empty = this.querySelector('.drawer__inner-empty');
    empty?.removeAttribute('hidden');
    this.querySelector('cart-drawer-items')?.classList.add('is-empty');
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = true;
    this.classList.remove(
      'prada-cart-drawer--multiple',
      'is-empty-pending',
      'is-empty-stable',
      'is-empty-revealed',
    );
    this.classList.add('is-empty');

    if (!animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.classList.remove('is-empty-transitioning', 'is-empty-revealed');
      this.classList.add('is-empty-stable');
      return;
    }

    this.classList.add('is-empty-transitioning');
    window.requestAnimationFrame(() => {
      if (this.classList.contains('is-empty')) this.classList.add('is-empty-revealed');
    });
    this.emptyTimer = window.setTimeout(() => {
      this.classList.remove('is-empty-transitioning', 'is-empty-revealed');
      this.classList.add('is-empty-stable');
    }, 320);
  }

  updateMultipleLayout() {
    // One vertical mobile layout is used for every cart size. Removing the
    // legacy mode classes also normalizes stale section HTML already in cache.
    this.classList.remove('prada-cart-drawer--multiple');
    this.querySelector('.prada-cart-drawer__items')?.classList.remove('prada-cart-drawer__items--multiple');
  }

  setDrawerBusy(isBusy) {
    const inner = this.querySelector('.drawer__inner');
    const items = this.querySelector('cart-drawer-items');
    if (isBusy) {
      inner?.setAttribute('aria-busy', 'true');
      items?.setAttribute('aria-busy', 'true');
      return;
    }
    inner?.removeAttribute('aria-busy');
    items?.removeAttribute('aria-busy');
  }

  formatMoney(cents, referenceText = '') {
    const locale = document.documentElement.lang || 'en-IN';
    const currency = window.Shopify?.currency?.active || 'INR';
    const number = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
    const prefix = referenceText.trim().match(/^[^\d-]+/)?.[0]?.trim();
    return prefix ? `${prefix} ${number}` : new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  showCartError(message) {
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (!errors) return;
    errors.textContent = message;
    window.setTimeout(() => {
      if (errors.textContent === message) errors.textContent = '';
    }, 5000);
  }

  requestCanonicalRefresh() {
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => this.refreshCanonicalWhenIdle(), 60);
  }

  refreshCanonicalWhenIdle() {
    if (window.PradaCartMutations?.pending) {
      window.PradaCartMutations.whenIdle().then(() => this.requestCanonicalRefresh());
      return;
    }
    if (this.refreshPromise) return;

    const requestedRevision = this.revision;
    const cartUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    cartUrl.searchParams.set('section_id', 'cart-drawer');
    const refreshPromise = fetch(cartUrl.toString(), {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (requestedRevision !== this.revision || window.PradaCartMutations?.pending) {
          this.requestCanonicalRefresh();
          return;
        }
        this.deferredCanonicalState = { sections: { 'cart-drawer': html } };
        if (!this.isVisible()) this.flushDeferredCanonicalSection();
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (this.refreshPromise === refreshPromise) this.refreshPromise = null;
      });
    this.refreshPromise = refreshPromise;
  }

  isVisible() {
    return this.classList.contains('active') || this.classList.contains('animate') || this.classList.contains('is-closing');
  }

  flushDeferredCanonicalSection() {
    if (!this.deferredCanonicalState || window.PradaCartMutations?.pending) return false;
    if (this.querySelector('.cart-item.is-removing, .cart-item.is-collapsing')) {
      window.clearTimeout(this.reconcileTimer);
      this.reconcileTimer = window.setTimeout(
        () => this.flushDeferredCanonicalSection(),
        PRADA_CART_ROW_TRANSITION_DURATION + 20,
      );
      return false;
    }
    const state = this.deferredCanonicalState;
    this.deferredCanonicalState = null;
    this.applyCanonicalSection(state.sections['cart-drawer']);
    return true;
  }

  applyCanonicalSection(html) {
    const sourceDrawer = this.getSectionDOM(html, 'cart-drawer');
    const sourceContents = sourceDrawer?.querySelector('#CartDrawer');
    const targetContents = this.querySelector('#CartDrawer');
    if (!sourceDrawer || !sourceContents || !targetContents) return false;

    targetContents.innerHTML = sourceContents.innerHTML;
    this.dataset.cartItemCount = sourceDrawer.dataset.cartItemCount || '0';
    this.dataset.cartTotalPrice = sourceDrawer.dataset.cartTotalPrice || '0';
    this.classList.remove(
      'is-empty-transitioning',
      'is-empty-revealed',
      'is-empty-entering',
      'is-empty-visible',
      'is-empty-leaving',
    );
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.classList.toggle('is-empty-stable', sourceDrawer.classList.contains('is-empty'));
    this.classList.remove('prada-cart-drawer--multiple');
    this.querySelector('.prada-cart-drawer__items')?.classList.remove('prada-cart-drawer__items--multiple');
    updatePradaCartIcon(this.dataset.cartItemCount);
    return true;
  }

  renderContents(parsedState, { shouldOpen = true } = {}) {
    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    if (sectionHtml) {
      if (this.isVisible() || window.PradaCartMutations?.pending) {
        this.deferredCanonicalState = { sections: { 'cart-drawer': sectionHtml } };
      } else {
        this.applyCanonicalSection(sectionHtml);
      }
    } else {
      this.requestCanonicalRefresh();
    }

    if (typeof parsedState?.item_count === 'number') updatePradaCartIcon(parsedState.item_count);
    if (shouldOpen) this.open();
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${CSS.escape(String(id))}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    if (quantity === 0 && event?.currentTarget instanceof CartRemoveButton) {
      this.closest('cart-drawer')?.removeItem(event.currentTarget, event);
      return;
    }
    super.updateQuantity(line, quantity, event, name, variantId);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
